import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button, Table, Input, Spinner, FormGroup, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import {
  newFetchTeacherByCourseId,
  transactionCourseChange,
  changeLevel,
} from '../../../../../Api/apiUtils';
import { PaymentConfigContext } from '../../../../../Api/global/ApiContext';
import { MixpanelEvents, trackEvent } from '../../../../../utils/mixpanelTracking';
import {
  calculateTotalPrice,
  buildToCourseDetail,
  buildSourcePayload,
  buildRemoveLevels,
  combineTransactions,
  firstFilledLevelId,
  changeErrorMessage,
  reportActiveLevelFailure,
  maxPurchasableClasses,
  reportChangeResult,
  sumRemainingClasses,
  sumRemainingValue,
} from './txnChangeShared';
import { PanelProps } from './course-change-panel';
import TeacherSelect from './TeacherSelect';

// Popup body for "Change Level": keep the SAME course, redistribute the selected transactions'
// remaining classes across its levels and set the active level.
const LevelChangePanel: React.FC<PanelProps> = ({ transactions, studentId, profileData, userType, onDone }) => {
  const transaction = useMemo(() => combineTransactions(transactions), [transactions]);
  const paymentConfig = useContext(PaymentConfigContext);
  const paymentCourses: any[] = paymentConfig?.onlineCourseAcademic2 || [];

  const [submitting, setSubmitting] = useState(false);
  const [toCourseDetail, setToCourseDetail] = useState<any>(null);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<any>('');

  const sameCourseId = transaction?.courseList?.[0]?.courseId;

  // Load the same course's levels/pricing once (class counts start at 0).
  useEffect(() => {
    if (!sameCourseId) return;
    (async () => {
      try {
        const detail = await buildToCourseDetail(paymentCourses, sameCourseId, paymentConfig, profileData?.timezone);
        if (detail) setToCourseDetail(detail);
      } catch (e: any) {
        console.error(e);
        toast.error(e?.message || 'Failed to fetch course levels');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameCourseId]);

  useEffect(() => {
    if (!sameCourseId) return;
    (async () => {
      try {
        const response = await newFetchTeacherByCourseId({ page: 0, size: 500, courseId: sameCourseId });
        setTeachers(Array.isArray(response?.data?.content) ? response.data.content : []);
      } catch (e) {
        setTeachers([]);
      }
    })();
  }, [sameCourseId]);

  const availableAmount = +sumRemainingValue(transaction).toFixed(2);
  const selectedLevelId = firstFilledLevelId(toCourseDetail);
  const activeLevelName = toCourseDetail?.levelList?.find((l: any) => l.levelId === selectedLevelId)?.level || '';
  const remainingClasses = sumRemainingClasses(transaction);
  const newLevelValue = toCourseDetail ? +(+toCourseDetail.discountedTotal).toFixed(2) : 0;
  const walletAmount = Math.max(0, +(availableAmount - newLevelValue).toFixed(2));
  const totalNewClasses = toCourseDetail
    ? toCourseDetail.levelList.reduce((acc: number, l: any) => acc + (+l.classCount || 0), 0)
    : 0;

  const handleClassCount = (levelId: string, value: string) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    const newLevelList = toCourseDetail.levelList.map((l: any) =>
      l.levelId === levelId ? { ...l, classCount: +sanitized } : l
    );
    setToCourseDetail(calculateTotalPrice({ ...toCourseDetail, levelList: newLevelList }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!toCourseDetail) return toast.error('Course levels not loaded');
    if (!selectedTeacher?.value) return toast.error('Select a teacher');
    if (totalNewClasses <= 0) return toast.error('Assign at least one class to a level');
    if (totalNewClasses > remainingClasses) return toast.error(`Cannot assign more than the ${remainingClasses} remaining classes`);
    if (newLevelValue > availableAmount + 0.5) return toast.error('New level value exceeds the available amount — not allowed');

    const removeLevels = buildRemoveLevels(transactions);

    const addLevels = toCourseDetail.levelList
      .filter((l: any) => l.classCount !== '' && +l.classCount !== 0)
      .map((l: any) => {
        const finalAmount = +((+l.unit_price * +l.classCount)).toFixed(2);
        return {
          level: l.level,
          levelId: l.levelId,
          courseId: l.courseId,
          courseName: l.courseName,
          noOfClasses: +l.classCount,
          unit_price: l.unit_price,
          totalDiscount: 0,
          totalFinalPrice: finalAmount,
        };
      });

    const courseId = sameCourseId;
    const Userdetails = JSON.parse(localStorage.getItem('profileData') || '{}');

    const payload = {
      studentId,
      userId: profileData?.userId,
      name: profileData?.firstName || transaction.name,
      email: transaction.email || profileData?.emailId,
      currency: transaction.currency || toCourseDetail.currency,
      customer_id: transaction.customer_id,
      centreId: transaction.centreId || '',
      place_of_supply: transaction.place_of_supply || '',
      userType: userType || transaction.studentType || 'PAID',
      conversionRate: transaction.conversionRate,
      changedBy: Userdetails?.firstName || '',
      isFreeClass: false,
      changeType: 'level',
      teacherId: selectedTeacher.value,
      activeLevelId: selectedLevelId,
      activeLevel: activeLevelName,
      remainingClasses,
      remainingValue: availableAmount,
      newCourseValue: newLevelValue,
      walletAmount,
      fromCourse: {
        courseId,
        courseName: transaction.courseList?.[0]?.courseName || '',
        removeLevels,
      },
      toCourse: {
        courseId,
        courseName: toCourseDetail.levelList?.[0]?.courseName || '',
        addLevels,
      },
      sources: transactions.map(buildSourcePayload),
    };

    setSubmitting(true);
    try {
      const response = await transactionCourseChange(payload);
      if (!response || response.success === false) {
        toast.error('Failed to change level');
        trackEvent(MixpanelEvents.LEVEL_CHANGE, {
          page: 'Transaction Level Change', studentId, userId: profileData?.userId,
          courseId, levelId: selectedLevelId, status: 'failed',
        });
        return;
      }
      try {
        await changeLevel({ userId: profileData?.userId, courseId, levelId: selectedLevelId, changeType: 'level' });
      } catch (e) {
        console.error('changeLevel failed', response?.traceId, e);
        reportActiveLevelFailure(response, activeLevelName);
      }
      trackEvent(MixpanelEvents.LEVEL_CHANGE, {
        page: 'Transaction Level Change', studentId, userId: profileData?.userId,
        courseId, levelId: selectedLevelId,
        newTransactionId: response.newTransactionId, walletAmount, status: 'success',
      });
      reportChangeResult(response, 'Level changed successfully!');
      onDone();
    } catch (error: any) {
      console.error('transaction level change error', error);
      toast.error(changeErrorMessage(error, 'Failed to change level'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h6>Available amount: <b>{availableAmount.toFixed(2)} {transaction.currency}</b> ({remainingClasses} classes)</h6>
      {!toCourseDetail && <div className="d-flex justify-content-center"><Spinner size="sm" color="primary" /></div>}
      {toCourseDetail && (
        <>
          <Table bordered>
            <thead>
              <tr>
                <th>Level</th>
                <th>Class Price</th>
                <th title="Most classes of this level that still fit in the available amount, after the classes entered for the other levels">Max Affordable Classes</th>
                <th>Class Count</th>
              </tr>
            </thead>
            <tbody>
              {toCourseDetail.levelList.map((l: any) => (
                <tr key={l.levelId}>
                  <td>{l.level}</td>
                  <td>{l.unit_price}</td>
                  <td>{maxPurchasableClasses(toCourseDetail, l.levelId, availableAmount)}</td>
                  <td style={{ width: 120 }}>
                    <Input type="text" value={l.classCount} onChange={(e) => handleClassCount(l.levelId, e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <FormGroup>
            <Label>Teacher</Label>
            <TeacherSelect teachers={teachers} value={selectedTeacher} onChange={setSelectedTeacher} />
          </FormGroup>

          <div style={{ marginTop: 10, padding: 10, background: '#f7f7f7', borderRadius: 8 }}>
            <div>New level value: <b>{newLevelValue.toFixed(2)} {toCourseDetail.currency}</b></div>
            <div>Active level (first level with classes): <b>{activeLevelName || '-'}</b></div>
            <div>Wallet credit (leftover): <b>{walletAmount.toFixed(2)} {toCourseDetail.currency}</b></div>
            {(totalNewClasses > remainingClasses || newLevelValue > availableAmount + 0.5) && (
              <div style={{ color: 'red' }}>New level value exceeds available amount — not allowed.</div>
            )}
          </div>

          <Button
            color="primary"
            style={{ marginTop: 15 }}
            disabled={submitting || totalNewClasses <= 0 || totalNewClasses > remainingClasses || newLevelValue > availableAmount + 0.5}
            onClick={handleSubmit}
          >
            {submitting ? 'Processing…' : 'Change Level'}
          </Button>
        </>
      )}
    </div>
  );
};

export default LevelChangePanel;
